import { theme } from '@prophecy/theme';
import { Button, Centered, isFormDirty, Stack } from '@prophecy/ui-v3';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { Text } from '@prophecy/ui-v3/Typography';
import { useOnMount } from '@prophecy/utils/react/hooks';
import { useState } from 'react';
import styled from 'styled-components';

import { ProviderFormProvider, useProviderForm } from './common/ProviderFormContext';
import { LivyJobSizes } from './databricksFabrics/LivyJobSizes';
import ManagedJobSizes from './databricksFabrics/ManagedJobSizes';
import { DatabricksJobSize } from './databricksJobSize';
import { DatabricksMetaInfoProvider } from './databricksJobSize/DatabricksMetainfoContext';
import { useFetchPolicy } from './databricksJobSize/useFetchPolicy';
import useIsOrchestrationFabric from './hooks/useIsOrchestrationFabric';
import useUpdateFabric from './hooks/useUpdateFabric';
import { FabricTokens } from './tokens';
import {
  DatabricksFabricData,
  DatabricksMetaInfo,
  DatabricksType,
  FABRIC_TABS,
  FabricData,
  FabricProviderType,
  FormContexts,
  JobSizeObj,
  SPARK_PROVIDER
} from './types';

const SaveButtonContainer = styled(Stack)`
  position: sticky;
  bottom: 0;
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.gray200};
  padding: ${theme.spaces.x16} 0;
  margin-top: ${theme.spaces.x24};
`;

type JobsTabProps = {
  formContexts: FormContexts;
  isLoading?: boolean;
  fabricId: string;
  teamId: string;
};

export function JobsTab({ formContexts, isLoading, fabricId, teamId }: JobsTabProps) {
  const fabricProviderType = formContexts.providers.watch('fabricProviderType');
  const provider = formContexts.providers.watch('provider');
  const fabricData = formContexts.providers.watch('fabricData');
  const { updateExistingFabric, isLoading: isSaving } = useUpdateFabric(fabricId, formContexts, true);
  const { isOrchestrationFabric } = useIsOrchestrationFabric(formContexts);

  const isDirty = isFormDirty(formContexts[FABRIC_TABS.providers], ['fabricProviderType', 'provider']);

  // Only show Jobs tab for Spark fabrics
  if (fabricProviderType !== FabricProviderType.Spark) {
    return (
      <Centered>
        <Stack gap={theme.spaces.x12} align='center'>
          <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
            Job configuration not available
          </Text>
          <Text level='sm' tone={theme.colors.gray600}>
            This tab is only available for Spark fabrics
          </Text>
        </Stack>
      </Centered>
    );
  }

  if (isLoading) {
    return (
      <Centered>
        <Spinner size='l' spinning />
      </Centered>
    );
  }

  const handleSave = () => {
    formContexts[FABRIC_TABS.providers].handleSubmit(async (formData) => {
      await updateExistingFabric();
      formContexts[FABRIC_TABS.providers].reset(formData);
    })();
  };

  return (
    <ProviderFormProvider
      isOrchestrationFabric={isOrchestrationFabric}
      formContext={formContexts.providers}
      fabricId={fabricId}
      originalFabricId={fabricId}
      teamId={teamId}
      updateFabricErrors={() => {}}
      orchestrationProviderInfo={{ dataplaneUrls: [] }}>
      <Stack height='100%' gap={theme.spaces.x16}>
        <JobsTabContent provider={provider} fabricData={fabricData!} />

        <SaveButtonContainer direction='horizontal' align='end'>
          <Button
            variant='primary'
            onClick={handleSave}
            loading={isSaving}
            disabled={!isDirty || isSaving}
            data-test-id='jobs-tab-save-button'>
            Save Job Sizes
          </Button>
        </SaveButtonContainer>
      </Stack>
    </ProviderFormProvider>
  );
}

// Inner component that uses useProviderForm hook
function JobsTabContent({ provider, fabricData }: { provider: string; fabricData: FabricData }) {
  const { fabricId } = useProviderForm();
  const [metainfo, setMetainfo] = useState<DatabricksMetaInfo>();
  const [fetchPolicyInfo] = useFetchPolicy(fabricId, true);

  // Fetch policy info on mount for Databricks
  useOnMount(() => {
    if (provider === SPARK_PROVIDER.DATABRICKS || provider === SPARK_PROVIDER.DATAPROC) {
      const databricksFabricData = fabricData as DatabricksFabricData;

      // Determine dbType from the databricks URL
      const databricksURL = databricksFabricData?.databricksCreds?.databricksURL || '';
      let dbType = DatabricksType.AWS; // default
      if (databricksURL.includes('gcp.databricks.com')) {
        dbType = DatabricksType.GCP;
      } else if (databricksURL.includes('azuredatabricks.net')) {
        dbType = DatabricksType.Azure;
      }

      fetchPolicyInfo(databricksFabricData, undefined)
        .then((unrestrictedPolicy) => {
          setMetainfo({
            dbType,
            policies: unrestrictedPolicy ? [unrestrictedPolicy] : [],
            instanceProfiles: [],
            unrestrictedPolicy,
            defaultJobSize: { default: '' }
          });
        })
        .catch(() => {
          setMetainfo({
            dbType,
            policies: [],
            instanceProfiles: [],
            unrestrictedPolicy: undefined,
            defaultJobSize: { default: '' }
          });
        });
    }
  });

  const renderJobSizes = () => {
    switch (provider) {
      case SPARK_PROVIDER.DATABRICKS:
      case SPARK_PROVIDER.DATAPROC: {
        const databricksURL = (fabricData as DatabricksFabricData)?.databricksCreds?.databricksURL || '';

        if (!metainfo) {
          return (
            <Centered>
              <Spinner size='l' spinning />
            </Centered>
          );
        }

        return (
          <Stack gap={FabricTokens.Form.fieldGap}>
            <DatabricksMetaInfoProvider {...metainfo} databricksURL={databricksURL}>
              <DatabricksJobSize />
            </DatabricksMetaInfoProvider>
          </Stack>
        );
      }

      case SPARK_PROVIDER.PROPHECY: {
        const jobSizesData = (fabricData?.jobSizesData || []) as JobSizeObj[];
        return <ManagedJobSizes data={jobSizesData} />;
      }

      case SPARK_PROVIDER.LIVY:
      case SPARK_PROVIDER.EMR:
        return <LivyJobSizes isEMR={provider === SPARK_PROVIDER.EMR} />;

      case SPARK_PROVIDER.SYNAPSE:
        return <LivyJobSizes isSynapse />;

      default:
        return (
          <Centered>
            <Text level='sm' tone={theme.colors.gray600}>
              Job configuration not available for this provider
            </Text>
          </Centered>
        );
    }
  };

  return (
    <Stack gap={FabricTokens.Form.fieldGap} padding={`0 0 ${theme.spaces.x32}`} height='100%'>
      {renderJobSizes()}
    </Stack>
  );
}
